import React from 'react';
import { colorValueToSolidCss } from '../fields/ColorPicker/colorUtils';
import { resolveAllStyles } from './StyleResolver';

/**
 * Interactive accordion component with open/close state.
 * Used only in public mode (not in editor iframe).
 *
 * @param {Object} props
 * @param {Object} props.component - The full component object
 */
export default function AccordionPublic({ component }) {
  const props = component.props || {};
  const items = props.items || [];

  // Get spacing values (support responsive format)
  const getSpacing = (spacingObj) => {
    if (!spacingObj) return { top: 16, bottom: 16, left: 16, right: 16, unit: 'px' };
    if (spacingObj.base) return spacingObj.base;
    return spacingObj;
  };

  const headerPadding = getSpacing(props.headerPadding);
  const contentPadding = getSpacing(props.contentPadding);

  // Get default open items from props
  const getDefaultOpenState = () => {
    const state = {};
    items.forEach((item, idx) => {
      if (item.defaultOpen) {
        state[idx] = true;
      }
    });
    if (Object.keys(state).length === 0 && items.length > 0 && !props.allowMultiple) {
      state[0] = true;
    }
    return state;
  };

  const [openItems, setOpenItems] = React.useState(getDefaultOpenState);

  const toggleItem = (index) => {
    setOpenItems(prev => {
      if (props.allowMultiple) {
        return { ...prev, [index]: !prev[index] };
      } else {
        return { [index]: !prev[index] };
      }
    });
  };

  const containerStyle = {
    ...resolveAllStyles(component, 'base'),
    backgroundColor: colorValueToSolidCss(props.backgroundColor, '#ffffff'),
    overflow: 'hidden',
  };

  return (
    <div style={containerStyle}>
      {items.map((item, idx) => {
        const isOpen = !!openItems[idx];
        const isLast = idx === items.length - 1;

        return (
          <div
            key={item.id || idx}
            style={{
              borderBottom: !isLast ? `${props.borderWidth || 1}px ${props.borderStyle || 'solid'} ${colorValueToSolidCss(props.borderColor, '#e5e7eb')}` : 'none',
              marginBottom: props.itemGap ? `${props.itemGap}px` : 0,
            }}
          >
            <button
              onClick={() => toggleItem(idx)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: props.iconPosition === 'left' ? 'row-reverse' : 'row',
                backgroundColor: colorValueToSolidCss(props.headerBackgroundColor, '#f9fafb'),
                color: colorValueToSolidCss(props.headerTextColor, '#1f2937'),
                fontFamily: props.headerFontFamily && props.headerFontFamily !== 'System Default' ? props.headerFontFamily : 'inherit',
                fontSize: `${props.headerFontSize || 16}px`,
                fontWeight: props.headerFontWeight || '600',
                padding: `${headerPadding.top}${headerPadding.unit} ${headerPadding.right}${headerPadding.unit} ${headerPadding.bottom}${headerPadding.unit} ${headerPadding.left}${headerPadding.unit}`,
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span>{item.title}</span>
              {props.iconType !== 'none' && (
                <span style={{
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </span>
              )}
            </button>

            {isOpen && (
              <div
                style={{
                  backgroundColor: colorValueToSolidCss(props.contentBackgroundColor, '#ffffff'),
                  color: colorValueToSolidCss(props.contentTextColor, '#4b5563'),
                  fontFamily: props.contentFontFamily && props.contentFontFamily !== 'System Default' ? props.contentFontFamily : 'inherit',
                  fontSize: `${props.contentFontSize || 14}px`,
                  padding: `${contentPadding.top}${contentPadding.unit} ${contentPadding.right}${contentPadding.unit} ${contentPadding.bottom}${contentPadding.unit} ${contentPadding.left}${contentPadding.unit}`,
                }}
              >
                {item.content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
